import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, ArrowLeft } from "lucide-react";
import AuthShell from "@/components/suffa/AuthShell";
import { useLanguage } from "@/lib/i18n";
import { useAuth } from "@/lib/AuthContext";
import { formatApiError } from "@/lib/api";
import "@/pages/Auth.css";

const Login = () => {
  const { t } = useLanguage();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [loading, setLoading] = useState(false);

  const validate = () => {
    const e = {};
    if (!email.trim()) e.email = "Please enter your email address.";
    else if (!/^\S+@\S+\.\S+$/.test(email.trim()))
      e.email = "Please enter a valid email address.";
    if (!password) e.password = "Please enter your password.";
    return e;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    setServerError("");
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    setLoading(true);
    try {
      const user = await login(email, password);
      navigate(user?.hasProfile ? "/my-profile" : "/create-profile");
    } catch (err) {
      setServerError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      footer={
        <div className="auth-footer-nav">
          <p className="auth-footer">
            {t("Don’t have an account?")}{" "}
            <a href="/register" data-testid="login-to-register" className="auth-footer-link">
              {t("Create an Account")}
            </a>
          </p>
          <a href="/" className="auth-back" data-testid="login-back-home">
            <ArrowLeft size={14} aria-hidden="true" />
            {t("Back to Home")}
          </a>
        </div>
      }
    >
      <h1 className="auth-title" data-testid="login-title">
        {t("Welcome Back")}
      </h1>
      <p className="auth-subtitle" data-testid="login-subtitle">
        {t("Sign in to your Suffa Matrimony account.")}
      </p>

      <form className="auth-form" noValidate onSubmit={onSubmit}>
        {serverError && (
          <p className="auth-error" data-testid="login-error" role="alert">
            {t(serverError)}
          </p>
        )}

        <div className="auth-field">
          <label className="auth-label" htmlFor="login-email">
            Email Address
          </label>
          <input
            id="login-email"
            type="email"
            className={`auth-input${errors.email ? " has-error" : ""}`}
            placeholder="you@example.com"
            autoComplete="email"
            value={email}
            data-testid="login-email"
            aria-invalid={!!errors.email}
            onChange={(e) => setEmail(e.target.value)}
          />
          {errors.email && (
            <p className="auth-error" data-testid="login-email-error">
              {t(errors.email)}
            </p>
          )}
        </div>

        <div className="auth-field">
          <label className="auth-label" htmlFor="login-password">
            Password
          </label>
          <div className="auth-password">
            <input
              id="login-password"
              type={showPw ? "text" : "password"}
              className={`auth-input${errors.password ? " has-error" : ""}`}
              placeholder={t("Your password")}
              autoComplete="current-password"
              value={password}
              data-testid="login-password"
              aria-invalid={!!errors.password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button
              type="button"
              className="auth-eye"
              data-testid="login-password-toggle"
              aria-label={showPw ? "Hide password" : "Show password"}
              onClick={() => setShowPw((v) => !v)}
            >
              {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && (
            <p className="auth-error" data-testid="login-password-error">
              {t(errors.password)}
            </p>
          )}
        </div>

        <div className="auth-row">
          <label className="auth-check">
            <input
              type="checkbox"
              checked={remember}
              data-testid="login-remember"
              onChange={(e) => setRemember(e.target.checked)}
            />
            {t("Remember me")}
          </label>
          <a href="/contact" className="auth-forgot" data-testid="login-forgot">
            {t("Forgot Password?")}
          </a>
        </div>

        <button
          type="submit"
          className="btn btn-solid auth-submit"
          data-testid="login-submit"
          disabled={loading}
        >
          {loading ? t("Signing in…") : t("Login")}
        </button>
      </form>
    </AuthShell>
  );
};

export default Login;
